import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Home, Heart, ShoppingCart, Wine, LogOut, Camera, Beaker, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Hide FAB on scan page since user is already there
  const showFab = !location.pathname.startsWith('/scan');

  const navItems = [
    { to: '/dashboard', icon: Home, label: 'Home' },
    { to: '/cocktails', icon: Wine, label: 'Cocktails' },
    { to: '/bar-basics', icon: Beaker, label: 'Basics' },
    { to: '/favorites', icon: Heart, label: 'Favorites' },
    { to: '/shopping-list', icon: ShoppingCart, label: 'Shopping' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Desktop Header */}
      <header className="desktop-nav fixed top-0 left-0 right-0 z-50 glass px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <NavLink to="/dashboard" className="flex items-center gap-2">
            <Wine className="w-6 h-6 text-[#D4AF37]" />
            <span className="font-playfair text-xl font-bold text-[#F5F5F5]">Mixed Up</span>
          </NavLink>
          
          <nav className="flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `nav-link flex items-center gap-2 text-sm font-medium ${isActive ? 'active' : ''}`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            {user?.is_premium ? (
              <span className="flex items-center gap-1 text-[#D4AF37] text-sm font-medium">
                <Crown className="w-4 h-4" /> Premium
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/pricing')}
                className="text-[#D4AF37] border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full"
                data-testid="upgrade-nav-btn"
              >
                <Crown className="w-4 h-4 mr-1" /> Upgrade
              </Button>
            )}
            <span className="text-[#A3A3A3] text-sm">Hi, {user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#A3A3A3] hover:text-[#F5F5F5]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-24 md:pb-8 md:pt-20">
        <Outlet />
      </main>

      {/* Scan FAB - hidden on scan page */}
      {showFab && (
        <button
          onClick={() => navigate('/scan')}
          className="scan-fab"
          data-testid="scan-fab"
          aria-label="Scan your bar"
        >
          <Camera className="w-7 h-7" />
        </button>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav glass rounded-t-2xl">
        <div className="flex items-center justify-around">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-colors ${
                  isActive ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
